import { css, useTheme } from "@emotion/react";
import Image from "next/image";
import { ComponentProps } from "react";
import { MixModal } from "./MixModal";
import { useRouter } from "next/router";

interface DarkModeReleaseModalProps extends ComponentProps<typeof MixModal> {
  onHideForToday?: () => void;
}

export const DarkModeReleaseModal = ({ onHideForToday, ...props }: DarkModeReleaseModalProps) => {
  const theme = useTheme();
  const router = useRouter();

  return (
    <MixModal {...props}>
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: row;
          align-items: stretch;
          width: 90%;
          max-width: 662px;
          min-height: 359px;
          border-radius: ${theme.shape.radius.small}px;
          overflow: hidden;

          @media (max-width: 768px) {
            flex-direction: column;
            max-width: 340px;
            min-height: auto;
          }
        `}
      >
        {/* Left Side - Moon Icon Section */}
        <Image src="/img/dark-release.png" alt="Dark Release" css={css`
          @media (max-width: 320px) {
            width: 290px;
            height: 359px;
          }
        `} width={343} height={359} />

        {/* Right Side - Content Section */}
        <div
          css={css`
            flex: 1;
            padding: 32px 16px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            background-color: ${theme.palette.secondary.contrast};

            @media (max-width: 768px) {
              padding: 15px 16px;
            }
          `}
        >
          <div css={css`padding: 0 5px;
            @media (max-width: 768px) {
            margin-bottom: 15px;
            }
            `}>
            {/* Title */}
            <h2
              css={css`
              ${theme.typography.h3}
              color: ${theme.palette.text.primary};
              margin: 0 0 8px 0;
              line-height: 1.3;
              font-weight: 700;

              @media (max-width: 768px) {
                font-size: 20px;
                margin-bottom: 6px;
              }
            `}
            >
              다크모드를 출시하였습니다
            </h2>

            {/* Subtitle */}
            <p
              css={css`
              ${theme.typography.body2}
              color: ${theme.palette.text.secondary};
              margin: 0 0 8px 0;
              line-height: 1.5;

              @media (max-width: 768px) {
                margin-bottom: 6px;
                font-size: 16px;
              }
            `}
            >
              눈이 편안한 화면으로 믹스를 즐겨보세요
            </p>
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: center;
              border-top: 1px solid ${theme.palette.grey[300]};
              border-bottom: 1px solid ${theme.palette.grey[300]};
              padding: 24px 5px;

              @media (max-width: 768px) {
                margin-bottom: 20px;
                font-size: 13px;
                border-bottom: none;
                padding: 20px 0px 0px 5px;
              }
            `}
          >
            <div css={css`${theme.typography.body1} color: ${theme.palette.text.primary}; font-weight: 300;`}>
              디바이스 설정값에 따라 자동 적용되며,<br /><span css={css`${theme.typography.subtitle2} color: ${theme.palette.text.primary}; font-weight: 600;`}>우측 상단에서</span> 자유롭게 켜고 끌 수 있어요
            </div>
          </div>

          {/* Action Button */}
          <div css={css`display: flex; flex-direction: column; align-items: center;`}>
            <button
              css={css`
              background-color: ${theme.palette.secondary.main};
              color: ${theme.palette.secondary.contrast};
              border: none;
              border-radius: 4px;
              padding: 12px 20px;
              font-size: 18px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s ease-in-out;
              min-width: 120px;
              width: 100%;
              height: 54px;

              &:hover {
                background-color: ${theme.palette.text.secondary};
              }

              @media (max-width: 768px) {
                padding: 10px 16px;
                font-size: 18px;
                min-width: 100px;
              }
            `}
              onClick={() => {
                router.push("https://support.mix.day/27ddb7f5-8f0a-8020-883e-caa16894c052");
              }}
            >
              자세히 알아보기
            </button>

            <button
              css={css`
              ${theme.typography.body3}
              color: ${theme.palette.text.disabled};
              margin-top: 10px;
              line-height: 1.9;
              text-decoration: underline;
              text-underline-offset: 3px;
              font-weight: 400;


              @media (max-width: 768px) {
                margin-top: 12px;
                font-size: 14px;
              }
            `}
              onClick={() => {
                onHideForToday?.();
              }}
            >
              일주일 동안 그만보기
            </button>
          </div>
        </div>
      </div>
    </MixModal>
  );
};
